import React, { useState, useEffect, useContext } from 'react';
import ProjectsListCard from '/src/Components/ProjectsListCard/ProjectsListCard';
import { MoveUp, Plus } from 'lucide-react';
import { Doughnut } from 'react-chartjs-2';
import { WindowSizeContext } from '/src/App';

import './style.css';

const PlanSummary = ({ selectedPlan, propertyData, handleCreateFirstPlan }) => {

    const { deviceType } = useContext(WindowSizeContext);

    const projectsMasterList = propertyData?.data?.recommended_projects?.projects_list || [];

    // State variables
    const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);


    useEffect(() => {

        if (selectedPlan !== null && selectedPlan !== undefined) {

            // Get the name of the plan
            const planName = selectedPlan.planName;

            // Get the plan progress
            const planProgress = selectedPlan.progress;

            // Get the created date of the plan
            const planCreatedDate = selectedPlan.epochTimestamp;

            // Turn the epochTimestamp into a human readable date (ex: April 16, 2025 @ 2:33 pm)
            const planCreatedDateHumanReadable = new Date(planCreatedDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true });

            // Get the projects in the plan
            const planProjects = selectedPlan.projectData;

            // Get the project data for each project in the plan and add the diyPreference to the project data
            const planProjectsWithDiyPreference = planProjects.map(planProject => {
                const projectDiyPreference = planProject.diyPreference;
                const projectData = projectsMasterList.find(project => project.project_id === planProject.project_id);
                return { ...projectData, diyPreference: projectDiyPreference };
            });


            // Get the total materials cost of the projects using the project_cost_materials key
            const totalMaterialsCost = planProjectsWithDiyPreference.reduce((acc, project) => acc + project.project_cost_materials, 0);

            // Get the total labor cost of the projects using the project_cost_labor key and ONLY for projects with diyPreference of 'pro'
            const totalLaborCost = planProjectsWithDiyPreference.reduce((acc, project) => {
                if (project.diyPreference === 'pro') {
                    return acc + project.project_cost_labor;
                }
                return acc;
            }, 0);

            // Get the total cost of the projects
            const totalCost = totalMaterialsCost + totalLaborCost;

            // Get the count of the projects where diyPreference is 'pro'
            const proProjectsCount = planProjectsWithDiyPreference.filter(project => project.diyPreference === 'pro').length;

            // Get the count of the projects where diyPreference is 'diy'
            const diyProjectsCount = planProjectsWithDiyPreference.filter(project => project.diyPreference === 'diy').length;

            // Get the count of the total projects
            const totalProjectsCount = planProjectsWithDiyPreference.length;

            // Get the total value of the projects using the project_payoff_by_scenario[0].by_total.total values
            const totalValue = planProjectsWithDiyPreference.reduce((acc, project) => acc + project.project_payoff_by_scenario[0].by_total.total, 0);

            // Get the total count of discounts by summing the lengths of the project_discounts_company_specific arrays
            const totalDiscountsCount = planProjectsWithDiyPreference.reduce((acc, project) => acc + project.project_discounts_company_specific.length, 0);

            // New payoff calculations
            const payoffData = {
                capital_freed: 0,
                property_value_upside: 0,
                opex: 0
            };

            // Aggregate payoff data across all projects
            planProjectsWithDiyPreference.forEach(project => {
                const financials = project.project_payoff_by_scenario[0];

                // Add capital freed
                payoffData.capital_freed += financials.by_value_type.capital_freed.total;

                // Add property value upside (sum of all years)
                payoffData.property_value_upside += financials.by_value_type.property_value_upside.by_year.reduce(
                    (sum, value) => sum + value, 0
                );

                // Add all opex values
                Object.values(financials.by_value_type.opex).forEach(category => {
                    payoffData.opex += category.total;
                });
            });

            // Calculate years to break even (use the earliest break-even point among all projects)
            const yearsToBreakEven = Math.min(
                ...planProjectsWithDiyPreference.map(project => {
                    const roi = project.project_payoff_by_scenario[0].roi_assessment;
                    const totalByYear = roi.capital_freed.by_year.map((val, idx) =>
                        val + roi.opex.by_year[idx] + roi.property_value_upside.by_year[idx]
                    );
                    return totalByYear.findIndex(val => val >= 1) + 1;
                })
            );

            // After calculating payoffData and other metrics, add the chart config
            const doughnutConfig = {
                data: {
                    labels: ['Sudden Costs Avoided', 'Property Value Upside', 'Lower Recurring Expenses'],
                    datasets: [{
                        data: [
                            payoffData.capital_freed,
                            payoffData.property_value_upside,
                            payoffData.opex
                        ],
                        backgroundColor: ['#7c1158', '#87bc45', '#0d88e6'],
                        borderWidth: 0
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    aspectRatio: 1,
                    plugins: {
                        legend: {
                            position: 'bottom',
                            labels: {
                                font: { family: 'inherit' },
                                padding: 20,
                                boxWidth: 15,
                                usePointStyle: true
                            }
                        },
                        datalabels: {
                            color: '#fff',
                            font: {
                                weight: 'bold',
                                size: 16,
                                family: 'inherit'
                            },
                            formatter: (value, ctx) => {
                                const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                                const percentage = ((value / total) * 100).toFixed(0);
                                return `${percentage}%`;
                            }
                        },
                        tooltip: {
                            callbacks: {
                                label: (context) => {
                                    const value = context.raw;
                                    const percentage = ((value / context.dataset.data.reduce((a, b) => a + b, 0)) * 100).toFixed(0);
                                    return `  $${value.toLocaleString()} (${percentage}%)`;
                                }
                            }
                        }
                    },
                    cutout: '60%'
                }
            };

            const outputPlanDetails = {
                planName: planName,
                planProgress: planProgress,
                planCreatedDate: planCreatedDateHumanReadable,
                planProjects: planProjectsWithDiyPreference,
                benefits: {
                    totalValue: totalValue
                },
                costs: {
                    totalMaterialsCost: totalMaterialsCost,
                    totalLaborCost: totalLaborCost,
                    totalCost: totalCost
                },
                counts: {
                    totalProjectsCount: totalProjectsCount,
                    proProjectsCount: proProjectsCount,
                    diyProjectsCount: diyProjectsCount,
                    totalDiscountsCount: totalDiscountsCount
                },
                payoff: {
                    totalValue: totalValue,
                    yearsToBreakEven: yearsToBreakEven,
                    breakdown: {
                        capital_freed: payoffData.capital_freed,
                        property_value_upside: payoffData.property_value_upside,
                        opex: payoffData.opex
                    },
                    doughnutConfig: doughnutConfig
                }
            }

            setSelectedPlanDetails(outputPlanDetails);
        }
    }, [selectedPlan]);

    const noPlansText = "Looks like you don't have any plans yet. Create your first plan to get started!";


    // Sort the planProjects by diyPreference alphabetically
    const sortedPlanProjects = selectedPlanDetails?.planProjects.sort((a, b) => a.diyPreference.localeCompare(b.diyPreference)) || [];

    return (
        <div className="taking-action-section-container">

            <div className="plan-summary-header">

                <h1>{selectedPlanDetails ? selectedPlanDetails.planName : "Plan Summary"}</h1>
                {selectedPlanDetails &&
                    <span>Created on {selectedPlanDetails.planCreatedDate}</span>
                }
            </div>
            {!selectedPlanDetails &&
                <>
                    <p dangerouslySetInnerHTML={{ __html: noPlansText }} />
                    <button className='taking-action-button-get-started-complete box-shadow-standard' onClick={handleCreateFirstPlan}>Create a Plan</button>
                </>
            }
            {selectedPlanDetails &&
                <div className={`plan-summary-container ${deviceType}`}>
                    <div className={`plan-summary-projects-container-left-column ${deviceType}`}>
                        <div className={`plan-summary-projects-container ${deviceType}`}>
                            <h3>Home Improvements You're Making</h3>
                            <div className={`plan-summary-projects-container-projects-list ${deviceType}`}>
                                {sortedPlanProjects.map(project => {

                                    const projectDiyPreference = project.diyPreference;
                                    const projectCostMaterials = project.project_cost_materials;
                                    const projectCostLabor = project.project_cost_labor;
                                    const projectCostTotal = projectDiyPreference === 'pro' ? projectCostMaterials + projectCostLabor : projectCostMaterials;

                                    return (
                                        <div className={`plan-summary-projects-container-projects-list-item ${deviceType}`}>

                                            <div className="plan-summary-projects-container-projects-list-item-details">
                                                <span>{project.diyPreference === 'pro' ? 'Hiring a Pro' : 'Doing It Yourself'}</span>
                                            </div>
                                            <ProjectsListCard
                                                key={project.project_id}
                                                project={project}
                                                showAllDetails={false}
                                                simulatorView={false}
                                                handleProjectClick={() => { }}
                                                isSelected={false}
                                                handleAddingProjectToSimulator={() => { }}
                                            />
                                            {/* <div className="plan-summary-projects-container-projects-list-item-move-up-icon">
                                                <MoveUp />
                                            </div> */}
                                            <div className={`plan-summary-projects-container-projects-list-item-cost-info ${deviceType}`}>
                                                <div className={`plan-summary-projects-container-projects-list-item-cost-total ${deviceType}`}>
                                                    <span className='plan-summary-projects-container-projects-list-item-cost-total-label'>Est. Total Cost</span>
                                                    <span className='plan-summary-projects-container-projects-list-item-cost-total-value'>${projectCostTotal}</span>
                                                </div>

                                                <div className={`plan-summary-projects-container-projects-list-item-cost-details-breakdown ${deviceType}`}>
                                                    <div className={`plan-summary-projects-container-projects-list-item-cost-details-item ${deviceType}`}>
                                                        <span className='plan-summary-projects-container-projects-list-item-cost-details-item-label'>Materials</span>
                                                        <span className='plan-summary-projects-container-projects-list-item-cost-details-item-value'>${projectCostMaterials}</span>
                                                    </div>
                                                    <div className={`plus-icon-container ${deviceType}`}>
                                                        <Plus />
                                                    </div>
                                                    <div className={`plan-summary-projects-container-projects-list-item-cost-details-item ${deviceType}`}>
                                                        <span className='plan-summary-projects-container-projects-list-item-cost-details-item-label'>Labor</span>
                                                        <span className={`plan-summary-projects-container-projects-list-item-cost-details-item-value ${projectDiyPreference}`}>${projectCostLabor}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                    <div className={`plan-summary-projects-container-right-column ${deviceType}`}>
                        <div className={`plan-summary-projects-container ${deviceType}`}>

                            <h3>Return We Are Hoping to Achieve</h3>
                            <div className={`plan-summary-value-container ${deviceType}`}>
                                <div className={`plan-summary-value-metrics ${deviceType}`}>
                                    <div className={`plan-summary-value-card ${deviceType}`}>
                                        <span className="value">${selectedPlanDetails.payoff.totalValue.toLocaleString()}</span>
                                        <span className="label">Value Over 10 Years</span>
                                    </div>
                                    <div className="plan-summary-value-card-vertical-divider" />

                                    <div className="plan-summary-value-card">
                                        <span className="value">{selectedPlanDetails.payoff.yearsToBreakEven}</span>
                                        <span className="label">Years to Break Even</span>
                                    </div>
                                </div>
                                <div className={`plan-summary-value-chart ${deviceType}`}>
                                    <Doughnut
                                        data={selectedPlanDetails.payoff.doughnutConfig.data}
                                        options={selectedPlanDetails.payoff.doughnutConfig.options}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            }
        </div>
    );
};

export default PlanSummary;